import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { FolderApiService } from '@affine/core/modules/folder-api';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import { OBJECT_ROLE_LABEL_KEYS } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { FolderRole, getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useMemo } from 'react';

import { PermissionEditor } from '../shared/permission-editor';
import type {
  PermissionEditorAdapter,
  PermissionEditorConfig,
  PermissionEntry,
} from '../shared/permission-editor-types';

interface FolderPermissionsEditorProps {
  folderId: string;
}

const FOLDER_PERMISSION_CONFIG: PermissionEditorConfig = {
  allRoles: [
    FolderRole.Owner,
    FolderRole.Admin,
    FolderRole.Editor,
    FolderRole.Reader,
  ],
  grantableRoles: [FolderRole.Admin, FolderRole.Editor, FolderRole.Reader],
  ownerRole: FolderRole.Owner,
  demoteOnTransferRole: FolderRole.Admin,
  defaultGrantRole: FolderRole.Editor,
  roleLabelKeys: OBJECT_ROLE_LABEL_KEYS,
  allowGroups: true,
  testIdPrefix: 'folder-permissions',
};

export const FolderPermissionsEditor = ({
  folderId,
}: FolderPermissionsEditorProps) => {
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const folderApiService = useService(FolderApiService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const workspaceId = workspaceService.workspace.id;

  const adapter = useMemo(
    (): PermissionEditorAdapter => ({
      async loadEntries(): Promise<PermissionEntry[]> {
        const [userResult, groupRoles, groups] = await Promise.all([
          folderApiService.getFolderUsers(workspaceId, folderId, 0, 50),
          folderApiService.getFolderGroupRoles(workspaceId, folderId),
          folderApiService.getGroups(workspaceId),
        ]);

        const groupNameById = new Map(groups.map(g => [g.id, g.name]));

        const userEntries: PermissionEntry[] = userResult.items.map(item => ({
          type: 'user' as const,
          id: item.user.id,
          targetId: item.user.id,
          name: item.user.name ?? '',
          email: item.user.email ?? '',
          avatarUrl: item.user.avatarUrl ?? null,
          role: item.role,
          isAreaBlocked: item.isAreaBlocked,
          areaBoundaryKind: item.areaBoundaryKind,
          areaBoundaryRootFolderId: item.areaBoundaryRootFolderId,
          areaBoundaryRootName: item.areaBoundaryRootName,
        }));

        const groupEntries: PermissionEntry[] = groupRoles.map(r => ({
          type: 'group' as const,
          id: r.id,
          targetId: r.groupId,
          name: groupNameById.get(r.groupId) ?? r.groupId,
          role: r.role,
          isAreaBlocked: r.isAreaBlocked,
          areaBoundaryKind: r.areaBoundaryKind,
          areaBoundaryRootFolderId: r.areaBoundaryRootFolderId,
          areaBoundaryRootName: r.areaBoundaryRootName,
        }));

        return [...userEntries, ...groupEntries];
      },

      async loadMembers(query: string) {
        if (!serverService.server) return [];

        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            take: 50,
            query: query || undefined,
          },
        });

        return (result.workspace?.members ?? []).map(member => ({
          id: member.id,
          name: member.name ?? '',
          email: member.email ?? '',
          avatarUrl: member.avatarUrl ?? null,
        }));
      },

      async loadGroups() {
        const groups = await folderApiService.getGroups(workspaceId);
        return groups.map(g => ({
          id: g.id,
          name: g.name,
          description: g.description ?? undefined,
        }));
      },

      async grantUser(userId: string, role: string) {
        await objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: userId, kind: 'user' },
          role,
          target: { id: folderId, kind: 'folder' },
        });
      },

      async grantGroup(groupId: string, role: string) {
        await objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: groupId, kind: 'group' },
          role,
          target: { id: folderId, kind: 'folder' },
        });
      },

      async updateRole(entry: PermissionEntry, newRole: string) {
        await objectPermissionMutation.execute({
          action: 'update',
          principal: { id: entry.targetId, kind: entry.type },
          role: newRole,
          target: { id: folderId, kind: 'folder' },
        });
      },

      async revoke(entry: PermissionEntry) {
        await objectPermissionMutation.execute({
          action: 'revoke',
          principal: { id: entry.targetId, kind: entry.type },
          target: { id: folderId, kind: 'folder' },
        });
      },
    }),
    [
      folderApiService,
      folderId,
      objectPermissionMutation,
      serverService.server,
      workspaceId,
    ]
  );

  return (
    <PermissionEditor config={FOLDER_PERMISSION_CONFIG} adapter={adapter} />
  );
};
